import React, { useState, useRef, useEffect, useId } from 'react';
import { MapPin, Loader2 } from 'lucide-react';

interface CityData {
  name: string;
  postcode: string;
  city: string;
  context: string;
  departmentCode?: string;
  citycode?: string;
}

interface CitySuggestion {
  label: string;
  properties: CityData;
}

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onCitySelect?: (data: CityData) => void;
  onDepartmentExtracted?: (departmentCode: string) => void;
  onSelectionCleared?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
}

export function CityAutocomplete({
  value,
  onChange,
  onCitySelect,
  onDepartmentExtracted,
  onSelectionCleared,
  onBlur,
  placeholder = "Tapez votre ville...",
  className = '',
  disabled = false,
  label,
  error,
  required,
  helperText
}: CityAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [debounceTimer, setDebounceTimer] = useState<number | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Fonction pour extraire le code département depuis le citycode (code INSEE)
  const extractDepartmentCode = (context: string, citycode: string): string => {
    // Priorité au citycode (code INSEE) — plus fiable que le contexte
    if (citycode && citycode.length >= 2) {
      if (citycode.startsWith('2A')) return '2A';
      if (citycode.startsWith('2B')) return '2B';
      const deptCode = citycode.substring(0, 2);
      if (['97', '98'].includes(deptCode)) {
        return citycode.substring(0, 3);
      }
      return deptCode;
    }

    // Fallback: extraire du contexte, ex: "74, Haute-Savoie, Auvergne-Rhône-Alpes"
    if (context) {
      const firstPart = context.split(',')[0].trim();
      if (/^\d{2,3}$/.test(firstPart)) {
        return firstPart;
      }
    }

    return '99';
  };

  const searchCities = async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      // Recherche spécifique aux communes
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(searchQuery)}&type=municipality&limit=8`
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la recherche de ville');
      }

      const data = await response.json();

      // Villes qui nécessitent un arrondissement (ex: Paris 1er, Lyon 1er, Marseille 1er)
      const citiesRequiringArrondissement = ['Paris', 'Lyon', 'Marseille'];

      const formattedSuggestions = data.features
        .filter((feature: any) => {
          // Exclure les résultats qui sont exactement une grande ville sans arrondissement
          const cityName = feature.properties.name;
          if (citiesRequiringArrondissement.includes(cityName)) {
            return false;
          }
          return true;
        })
        .map((feature: any) => ({
          label: feature.properties.label,
          properties: {
            name: feature.properties.name,
            postcode: feature.properties.postcode,
            city: feature.properties.city,
            context: feature.properties.context,
            citycode: feature.properties.citycode
          }
        }));

      setSuggestions(formattedSuggestions);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Erreur lors de la recherche de ville:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/[0-9]/g, '');
    setQuery(newValue);
    onChange(newValue);
    onSelectionCleared?.();

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      searchCities(newValue);
    }, 300);
    
    setDebounceTimer(timer);
  };

  const handleSuggestionClick = (suggestion: CitySuggestion) => {
    const departmentCode = extractDepartmentCode(
      suggestion.properties.context, 
      suggestion.properties.citycode || ''
    );
    
    const enrichedData = {
      ...suggestion.properties,
      departmentCode
    };
    
    setQuery(suggestion.properties.city);
    setShowSuggestions(false);
    onChange(suggestion.properties.city);
    onCitySelect?.(enrichedData);
    onDepartmentExtracted?.(departmentCode);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        onBlur?.();
      }
    }, 150);
  };

  const id = useId();
  const inputClasses = `input-field pr-10 ${error ? 'input-error' : ''} ${className}`;

  return (
    <div className="field-group">
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="form-label-required">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className={inputClasses}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          aria-autocomplete="list"
          aria-expanded={showSuggestions && suggestions.length > 0}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
          ) : (
            <MapPin className="h-4 w-4 text-neutral-400" />
          )}
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-lg shadow-lg max-h-60 overflow-auto"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 transition-colors flex items-center gap-2 ${
                  selectedIndex === index ? 'bg-neutral-50' : ''
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <MapPin className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-neutral-900">
                    {suggestion.properties.city}
                  </div>
                  {suggestion.properties.postcode && (
                    <div className="text-xs text-neutral-500">
                      {suggestion.properties.postcode} • {suggestion.properties.context}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {helperText && !error && (
        <p className="helper-text">{helperText}</p>
      )}

      {error && (
        <p id={`${id}-error`} className="error-message">{error}</p>
      )}
    </div>
  );
}